import { PublicClientApplication, SilentRequest, SsoSilentRequest, AuthenticationResult, Configuration, LogLevel, AccountInfo, InteractionRequiredAuthError, RedirectRequest, PopupRequest, EndSessionRequest } from "@azure/msal-browser";

/**
 * Configuration class for @azure/msal-browser: 
 * https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_config_configuration_.html
 */
const MSAL_CONFIG: Configuration = {
    auth: {
        clientId: process.env.VUE_APP_CLIENTID,
        authority: `https://login.microsoftonline.com/${process.env.VUE_APP_TENANTID}`,
        postLogoutRedirectUri: process.env.VUE_APP_REDIRECT_URI,
        redirectUri: process.env.VUE_APP_REDIRECT_URI
    },
    cache: {
        cacheLocation: "localStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {	
                    return;	
                }	
                switch (level) {	
                    case LogLevel.Error:	
                        console.error(message);	
                        return;	
                    case LogLevel.Info:	
                        //console.info(message);	
                        return;	
                    case LogLevel.Verbose:	
                        //console.debug(message);	
                        return;	
                    case LogLevel.Warning:	
                        console.warn(message);	
                        return;	
                }
            }
        }
    }
};

/**
 * AuthModule for application - handles authentication in app.
 */
export class AuthModule {

    private MSALObj: PublicClientApplication; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/classes/_src_app_publicclientapplication_.publicclientapplication.html
    private account: AccountInfo; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-common/modules/_src_account_accountinfo_.html
    private loginRedirectRequest: RedirectRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_redirectrequest_.html
    private loginRequest: PopupRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_popuprequest_.html
    private silentLoginRequest: SsoSilentRequest;
    private apiRequest!: PopupRequest;
    private silentapiRequest!: SilentRequest;
    public authenticating: boolean;
    public redirectPromise: any;

    constructor() {
        this.MSALObj = new PublicClientApplication(MSAL_CONFIG);
        this.account = null;
        this.setRequestObjects();
    }

    public get isLoggedIn(): boolean{
        if(this.account){
            return true;
        }
        return false;
    }

    public get userName(): string {
        if(this.account){
            return this.account.username;
        }
        return "";
    }

    /**
     * Initialize request objects used by this AuthModule.
     */
    private setRequestObjects(): void {
        this.loginRequest = {
            scopes: ["openid", "profile"],
        };

        this.loginRedirectRequest = {
            ...this.loginRequest,
            redirectStartPage: window.location.href
        };        

        this.silentLoginRequest = {
            loginHint: "IDLAB@msidlab0.ccsctp.net"
        }

        // Add here scopes for access token to be used at MS Graph API endpoints.
        this.apiRequest = {
            scopes: ["openid", "profile", process.env.VUE_APP_ROOT_APISCOPE]
        };
        this.silentapiRequest = {
            scopes: ["openid", "profile", process.env.VUE_APP_ROOT_APISCOPE], 
            account: null,
            forceRefresh: false
        };
    }

    /**
     * Calls getAllAccounts and determines the correct account to sign into, currently defaults to first account found in cache.
     * TODO from msalSample: Add account chooser code
     * 
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */
    private getAccount(): AccountInfo {
        const currentAccounts = this.MSALObj.getAllAccounts();
        if (currentAccounts === null) {
            return null;
        }

        if (currentAccounts.length > 1) {
            // Add choose account code here
            console.log("Multiple accounts detected, picking first account.");
            return currentAccounts[0];
        } else if (currentAccounts.length === 1) {
            return currentAccounts[0];
        }
    }

    /**
     * Checks whether we are in the middle of a redirect and handles state accordingly. Only required for redirect flows.
     * 
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/initialization.md#redirect-apis
     */
    loadAuthModule(): void {
        this.MSALObj.handleRedirectPromise().then((resp: AuthenticationResult) => {
            this.handleResponse(resp);
        }).catch(console.error);
    }

    /**
     * Handles the response from a popup or redirect. If response is null, will check if we have any accounts and attempt to sign in.
     * @param response 
     */
    handleResponse(response: AuthenticationResult) {
        if (response !== null) {
            this.account = response.account;
        } else {
            this.account = this.getAccount();
        }
    }

    /**
     * Calls ssoSilent to attempt silent flow. If it fails due to interaction required error, it will prompt the user to login using popup.
     * @param request 
     */
    attemptSsoSilent() {
        this.MSALObj.ssoSilent(this.silentLoginRequest).then(() => {
            this.account = this.getAccount();
        }).catch((error: any) => {
            console.error("Silent Error: " + error);
            if (error instanceof InteractionRequiredAuthError) {
                this.login("loginPopup");
            }
        })
    }

    /**
     * Calls loginPopup or loginRedirect based on given signInType.
     * @param signInType 
     */
    async login(signInType: string): Promise<void> {
        this.authenticating = true;
        var account = this.getAccount()
        if(account){
            //this part prevents going through the authentication flow on page refresh
            this.account = account;
            return;
        }
        if (signInType === "loginPopup") {
            return this.MSALObj.loginPopup(this.loginRequest).then((resp: AuthenticationResult) => {
                this.handleResponse(resp);
                this.authenticating = false;
            }).catch(error => {
                console.log(error);
                this.authenticating = false;
            });
        } else if (signInType === "loginRedirect") {
            this.MSALObj.handleRedirectPromise().then((resp: AuthenticationResult) => {
                this.handleResponse(resp)
                if(!resp){
                    this.MSALObj.loginRedirect(this.loginRedirectRequest);
                }
                this.authenticating = false;
            });
            
        }
    }

    /**
     * Logs out of current account.
     */
    logout(): void {
        const logOutRequest: EndSessionRequest = {
            account: this.account
        };

        this.MSALObj.logout(logOutRequest);
    }

    /**
     * Gets the token to read api data from MS Graph silently, or falls back to interactive popup.
     */
    async getapiTokenPopup(forceRefresh: boolean = false): Promise<string> {
        if(!this.account){
            this.attemptSsoSilent();
        }
        this.silentapiRequest.account = this.account;
        this.silentapiRequest.forceRefresh = forceRefresh;
        var result = await this.getTokenPopup(this.silentapiRequest, this.apiRequest);
        this.silentapiRequest.forceRefresh = false;
        return result;
    }

    getapiTokenRedirect(forceRefresh: boolean = false): Promise<string> {
        if(!this.account){
            this.attemptSsoSilent();
        }
        this.silentapiRequest.account = this.account;
        this.silentapiRequest.forceRefresh = forceRefresh;
        var result = this.getTokenRedirect(this.silentapiRequest, this.apiRequest);
        this.silentapiRequest.forceRefresh = false;
        return result;
    }


    /**
     * Gets a token silently, or falls back to interactive popup.
     */
    private async getTokenPopup(silentRequest: SilentRequest, interactiveRequest: PopupRequest): Promise<string> {
        try {
            const response: AuthenticationResult = await this.MSALObj.acquireTokenSilent(silentRequest);
            return response.accessToken;
        } catch (e) {
            console.log("silent token acquisition fails.");
            if (e instanceof InteractionRequiredAuthError) {
                console.log("acquiring token using redirect");
                return this.MSALObj.acquireTokenPopup(interactiveRequest).then((resp: { accessToken: any; }) => {
                    return resp.accessToken;
                }).catch((err: any): any => {
                        console.error(err);
                        return null;
                    });
            } else {
                console.error(e);
            }
        }
    }

    /**
     * Gets a token silently, or falls back to interactive popup.
     */
     private async getTokenRedirect(silentRequest: SilentRequest, interactiveRequest: RedirectRequest): Promise<string> {
        try {
            const response: AuthenticationResult = await this.MSALObj.acquireTokenSilent(silentRequest);
            return response.accessToken;
        } catch (e) {
            console.log("silent token acquisition fails.");
            if (e instanceof InteractionRequiredAuthError) {
                console.log("acquiring token using redirect");
                return this.MSALObj.acquireTokenRedirect(interactiveRequest).then((resp: any) => {
                    return resp.accessToken;
                }).catch((err: any): any => {
                        console.error(err);
                        return null;
                    });
            } else {
                console.error(e);
            }
        }
    }
}
